import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { doc, setDoc, serverTimestamp, collection, getDocs, query, where, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { signUpWithEmailPassword } from '../../services/authService';
import { linkPhoneToUid, getUserByPhone } from '../../services/userProfileService';
import { IoChevronBack } from 'react-icons/io5';

const ACCENT = "#4A6741";


const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const verifiedPhone = location.state?.verifiedPhone || '';
  const fromSocial = location.state?.fromSocial || false;
  const socialEmail = location.state?.socialEmail || '';
  const socialName = location.state?.socialName || '';
  const socialUid = location.state?.socialUid || '';
  const socialProvider = location.state?.socialProvider || '';
  const existingAccount = location.state?.existingAccount || false;

  useEffect(() => {
    if (!verifiedPhone) {
      navigate('/phone-verify', { replace: true });
    }
  }, [verifiedPhone, navigate]);

  const [form, setForm] = useState({
    name: socialName,
    loginId: '',
    password: '',
    passwordConfirm: '',
    companyName: '',
    representative: '',
    companyAddress: '',
    companyAddressDetail: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [idChecked, setIdChecked] = useState(false);
  const [idChecking, setIdChecking] = useState(false);
  const [idAvailable, setIdAvailable] = useState(false);
  const [showAddr, setShowAddr] = useState(false);
  const addrRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'loginId') {
      setIdChecked(false);
      setIdAvailable(false);
    }
  };

  const loadDaumPostcode = useCallback(() => {
    return new Promise((resolve) => {
      if (window.daum?.Postcode) { resolve(); return; }
      const s = document.createElement('script');
      s.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      s.onload = () => resolve();
      document.head.appendChild(s);
    });
  }, []);

  const openAddressSearch = useCallback(async () => {
    setShowAddr(true);
    await loadDaumPostcode();
    setTimeout(() => {
      if (!addrRef.current || !window.daum?.Postcode) return;
      new window.daum.Postcode({
        oncomplete: (data) => {
          const addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
          setForm(prev => ({ ...prev, companyAddress: addr }));
          setShowAddr(false);
        },
        width: '100%',
        height: '100%',
      }).embed(addrRef.current);
    }, 100);
  }, [loadDaumPostcode]);

  const handleCheckId = async () => {
    if (!form.loginId.trim()) return;
    setIdChecking(true);
    try {
      const q = query(collection(db, 'users'), where('loginId', '==', form.loginId.trim()));
      const snap = await getDocs(q);
      setIdChecked(true);
      setIdAvailable(snap.empty);
    } catch (err) {
      console.error('중복 체크 실패:', err);
    } finally {
      setIdChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (existingAccount) {
      // 기존 계정에 아이디/비번 추가만
      if (!form.loginId) { setError('아이디를 입력해주세요.'); return; }
      if (!idChecked || !idAvailable) { setError('아이디 중복 확인을 해주세요.'); return; }
      if (!form.password) { setError('비밀번호를 입력해주세요.'); return; }
      if (form.password !== form.passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
      if (form.password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    } else {
      if (!form.name) { setError('이름을 입력해주세요.'); return; }
      if (!form.companyName.trim()) { setError('업체명을 입력해주세요.'); return; }
      if (!form.representative.trim()) { setError('대표자명을 입력해주세요.'); return; }
      if (!form.companyAddress.trim()) { setError('업체 주소를 입력해주세요.'); return; }
      if (!fromSocial) {
        if (!form.loginId) { setError('아이디를 입력해주세요.'); return; }
        if (!idChecked || !idAvailable) { setError('아이디 중복 확인을 해주세요.'); return; }
        if (!form.password) { setError('비밀번호를 입력해주세요.'); return; }
        if (form.password !== form.passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
        if (form.password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
      }
    }

    setLoading(true);
    try {
      if (existingAccount) {
        // 기존 계정에 아이디/비밀번호 추가 (PhoneVerifyPage에서 기존 계정 확인됨)
        const existingUser = await getUserByPhone(verifiedPhone);
        if (!existingUser) { setError('기존 계정을 찾을 수 없습니다.'); setLoading(false); return; }

        const fakeEmail = `${form.loginId.trim()}@jogunbiz.local`;
        const firebaseUser = await signUpWithEmailPassword({ email: fakeEmail, password: form.password });

        await setDoc(doc(db, 'users', existingUser.uid), {
          loginId: form.loginId.trim(),
          password: form.password,
          linkedEmailUid: firebaseUser.uid,
          providers: arrayUnion('email'),
          updatedAt: serverTimestamp(),
        }, { merge: true });

        navigate('/login', {
          replace: true,
          state: { toast: '아이디/비밀번호가 추가되었습니다.\n기존 계정으로 로그인하세요.' },
        });
      } else {
        // 새 계정 생성
        let uid;

        if (fromSocial) {
          uid = socialUid;
        } else {
          const fakeEmail = `${form.loginId.trim()}@jogunbiz.local`;
          const firebaseUser = await signUpWithEmailPassword({ email: fakeEmail, password: form.password });
          uid = firebaseUser.uid;
        }

        const providers = [];
        if (!fromSocial) providers.push('email');
        if (fromSocial && socialProvider) providers.push(socialProvider === 'google.com' ? 'google' : socialProvider);

        const userData = {
          loginId: fromSocial ? '' : form.loginId.trim(),
          password: fromSocial ? '' : form.password,
          email: fromSocial ? socialEmail : '',
          name: form.name,
          role: '',
          provider: providers[0] || '',
          providers,
          phoneE164: verifiedPhone,
          phoneVerified: true,
          status: 'pending',
          createdAt: serverTimestamp(),
        };

        userData.companyName = form.companyName.trim();
        userData.representative = form.representative.trim();
        userData.companyAddress = form.companyAddress.trim();
        userData.companyAddressDetail = form.companyAddressDetail.trim();

        await setDoc(doc(db, 'users', uid), userData);
        await linkPhoneToUid({ uid, phoneE164: verifiedPhone });

        navigate('/login', {
          replace: true,
          state: { toast: '회원가입이 완료되었습니다.\n관리자 승인 후 이용 가능합니다.' },
        });
      }
    } catch (err) {
      console.error('회원가입 실패:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('이미 사용 중인 아이디입니다.');
      } else {
        setError('회원가입에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {showAddr && (
        <AddrOverlay>
          <AddrHeader2>
            <AddrHeaderTitle>주소 검색</AddrHeaderTitle>
            <AddrCloseBtn onClick={() => setShowAddr(false)}>닫기</AddrCloseBtn>
          </AddrHeader2>
          <AddrEmbed ref={addrRef} />
        </AddrOverlay>
      )}
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <IoChevronBack size={24} />
        </BackButton>
        <HeaderTitle>{existingAccount ? '아이디/비밀번호 추가' : '회원가입'}</HeaderTitle>
      </Header>

      <FormArea onSubmit={handleSubmit}>
        {existingAccount ? (
          <ExistingNotice>이미 가입된 계정이 있습니다. 아이디와 비밀번호를 추가하면 아이디로도 로그인할 수 있습니다.</ExistingNotice>
        ) : (
          <>
            <Label>이름</Label>
            <Input
              name="name"
              placeholder="이름을 입력하세요"
              value={form.name}
              onChange={handleChange}
            />
          </>
        )}

        {(!fromSocial || existingAccount) && (
          <>
            <Label>아이디</Label>
            <IdRow>
              <IdInput
                name="loginId"
                type="text"
                placeholder="아이디를 입력하세요"
                value={form.loginId}
                onChange={handleChange}
              />
              <DupCheckBtn type="button" onClick={handleCheckId} disabled={idChecking || !form.loginId.trim()}>
                {idChecking ? '확인중' : '중복확인'}
              </DupCheckBtn>
            </IdRow>
            {idChecked && (
              <DupResult $ok={idAvailable}>
                {idAvailable ? '사용 가능한 아이디입니다.' : '이미 사용 중인 아이디입니다.'}
              </DupResult>
            )}

            <Label>비밀번호</Label>
            <Input
              name="password"
              type="password"
              placeholder="6자 이상 입력하세요"
              value={form.password}
              onChange={handleChange}
            />

            <Label>비밀번호 확인</Label>
            <Input
              name="passwordConfirm"
              type="password"
              placeholder="비밀번호를 다시 입력하세요"
              value={form.passwordConfirm}
              onChange={handleChange}
            />
          </>
        )}

        {!existingAccount && (
          <>
        <SectionDivider />
        <SectionLabel>업체 정보</SectionLabel>

            <Label>업체명</Label>
            <Input
              name="companyName"
              placeholder="업체명을 입력하세요"
              value={form.companyName}
              onChange={handleChange}
            />

            <Label>대표자명</Label>
            <Input
              name="representative"
              placeholder="대표자명을 입력하세요"
              value={form.representative}
              onChange={handleChange}
            />

            <Label>업체 주소</Label>
            <AddrRow>
              <AddrInput
                name="companyAddress"
                placeholder="주소 검색을 눌러주세요"
                value={form.companyAddress}
                readOnly
                onClick={openAddressSearch}
              />
              <AddrBtn type="button" onClick={openAddressSearch}>검색</AddrBtn>
            </AddrRow>
            <Input
              name="companyAddressDetail"
              placeholder="상세주소를 입력하세요"
              value={form.companyAddressDetail}
              onChange={handleChange}
              style={{ marginTop: 8 }}
            />
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (existingAccount ? '추가 중...' : '가입 중...') : (existingAccount ? '아이디/비밀번호 추가' : '회원가입')}
        </SubmitButton>
      </FormArea>
    </Container>
  );
};

export default SignUpPage;

const Container = styled.div`
  min-height: 100vh;
  background: #F5F7FA;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  background: #1a1d29;
  color: white;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  color: white;
`;

const HeaderTitle = styled.p`
  font-size: 17px;
  color: white;
`;

const FormArea = styled.form`
  padding: 16px 24px 40px;
  margin: 16px;
  background: white;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #333;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: #E5E7EB;
  margin: 20px 0 4px;
`;

const SectionLabel = styled.p`
  font-size: 13px;
  color: #9CA3AF;
  margin-top: 8px;
`;

const IdRow = styled.div`
  display: flex;
  gap: 8px;
`;

const IdInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 15px;
  background: white;
  &::placeholder { color: #9CA3AF; }
  &:focus { border-color: ${ACCENT}; }
`;

const DupCheckBtn = styled.button`
  padding: 0 14px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: #6B7280;
  font-size: 13px;
  border: 1px solid #E5E7EB;
  white-space: nowrap;
  &:disabled { opacity: 0.5; }
`;

const DupResult = styled.p`
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  color: ${({ $ok }) => $ok ? '#22C55E' : '#EF4444'};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 15px;
  background: white;
  &::placeholder { color: #9CA3AF; }
  &:focus { border-color: ${ACCENT}; }
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 13px;
  margin-top: 8px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #3B5534, #4A6741);
  color: white;
  font-size: 15px;
  margin-top: 24px;
  &:disabled { opacity: 0.6; }
`;

const AddrOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const AddrHeader2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #E5E7EB;
`;

const AddrHeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #1E2A32;
`;

const AddrCloseBtn = styled.button`
  font-size: 14px;
  color: #5A6B75;
`;

const AddrEmbed = styled.div`
  flex: 1;
`;

const AddrRow = styled.div`
  display: flex;
  gap: 8px;
`;

const AddrInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 15px;
  background: #FAFAFA;
  cursor: pointer;
  &::placeholder { color: #9CA3AF; }
`;

const AddrBtn = styled.button`
  flex-shrink: 0;
  padding: 0 16px;
  border-radius: 8px;
  background: ${ACCENT};
  color: white;
  font-size: 14px;
  white-space: nowrap;
`;

const ExistingNotice = styled.div`
  padding: 14px 16px;
  background: #EDF2EB;
  border-radius: 10px;
  font-size: 14px;
  color: #3D6634;
  line-height: 1.5;
  margin-bottom: 4px;
`;
